export type CelestialMode = 'landing' | 'funnel' | 'loading' | 'none';
export type CelestialRenderingProfileName = 'constrained' | 'balanced' | 'full';

export interface CelestialRenderingProfile {
  name: CelestialRenderingProfileName;
  dprCap: number;
  starCounts: Record<Exclude<CelestialMode, 'none'>, number>;
  trailRates: Record<Exclude<CelestialMode, 'none'>, number>;
  glowRate: number;
  shadowScale: number;
  landingShootIntervalMs: number;
  funnelShootIntervalMs: number;
}

type Star = { x:number;y:number;r:number;phase:number;twinkle:number;fall:number;drift:number;silver:boolean;trail:boolean;glow:boolean };
type Nebula = { x:number;y:number;radius:number;color:string };
type Shoot = { x:number;y:number;length:number;angle:number;age:number;duration:number;silver:boolean };

const profiles: Record<CelestialRenderingProfileName, CelestialRenderingProfile> = {
  constrained: {
    name: 'constrained', dprCap: 1.5,
    starCounts: { landing: 150, funnel: 120, loading: 96 },
    trailRates: { landing: .045, funnel: 0, loading: .07 },
    glowRate: .24, shadowScale: 1.35,
    landingShootIntervalMs: 5600, funnelShootIntervalMs: 5200,
  },
  balanced: {
    name: 'balanced', dprCap: 1.75,
    starCounts: { landing: 230, funnel: 195, loading: 138 },
    trailRates: { landing: .075, funnel: 0, loading: .11 },
    glowRate: .58, shadowScale: 2,
    landingShootIntervalMs: 3800, funnelShootIntervalMs: 4000,
  },
  full: {
    name: 'full', dprCap: 2,
    starCounts: { landing: 320, funnel: 280, loading: 180 },
    trailRates: { landing: .1, funnel: 0, loading: .15 },
    glowRate: 1, shadowScale: 2.8,
    landingShootIntervalMs: 2600, funnelShootIntervalMs: 3000,
  },
};

export function selectCelestialRenderingProfile(viewport: { width:number;height:number;devicePixelRatio?:number }): CelestialRenderingProfile {
  const width = Math.max(1, viewport.width);
  const height = Math.max(1, viewport.height);
  const shortEdge = Math.min(width, height);
  const pixelArea = width * height * Math.min(Math.max(viewport.devicePixelRatio ?? 1, 1), 2) ** 2;
  if (shortEdge <= 600 || pixelArea <= 1_100_000) return profiles.constrained;
  if (shortEdge <= 800 || pixelArea <= 3_000_000) return profiles.balanced;
  return profiles.full;
}

export function shouldAnimateCelestial(reducedMotion: boolean, pageHidden: boolean): boolean {
  return !reducedMotion && !pageHidden;
}

const landingNebulas: Nebula[] = [
  {x:.25,y:.12,radius:.22,color:'rgba(70,35,130,.07)'},
  {x:.72,y:.55,radius:.28,color:'rgba(35,55,130,.06)'},
  {x:.48,y:.88,radius:.20,color:'rgba(130,70,35,.05)'},
  {x:.08,y:.45,radius:.18,color:'rgba(50,35,110,.06)'},
  {x:.90,y:.45,radius:.16,color:'rgba(35,80,130,.05)'},
];

let controller: { setMode:(mode:CelestialMode)=>void;stop:()=>void } | undefined;
let requestedMode: CelestialMode = 'landing';

export function setCelestialMode(mode: CelestialMode): void {
  requestedMode = mode;
  controller?.setMode(mode);
}

export function startCelestialCanvas(): () => void {
  if (controller) return controller.stop;
  const canvas = document.querySelector<HTMLCanvasElement>('#celestial-canvas');
  const context = canvas?.getContext('2d');
  if (!canvas || !context) return () => {};

  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  let mode = requestedMode;
  let profile = selectCelestialRenderingProfile({ width: innerWidth, height: innerHeight, devicePixelRatio });
  let stars: Star[] = [];
  let shoots: Shoot[] = [];
  let initialShoots: number[] = [];
  let nextShoot = Infinity;
  let frame = 0;
  let width = 0;
  let height = 0;
  let ratio = 1;
  let previous = performance.now();
  let cachedLandingBackground: HTMLCanvasElement | undefined;

  const shootInterval = (): number => mode === 'landing' ? profile.landingShootIntervalMs : profile.funnelShootIntervalMs;

  const build = (): void => {
    const count = mode === 'none' ? 0 : profile.starCounts[mode];
    const trailRate = mode === 'none' ? 0 : profile.trailRates[mode];
    stars = Array.from({length:count}, () => ({
      x:Math.random()*1.2-.1,
      y:Math.random()*1.2-.1,
      r:.2+Math.random()*(mode==='landing'?1.6:1.5),
      phase:Math.random()*Math.PI*2,
      twinkle:.6+Math.random()*1.5,
      fall:mode==='loading'?.05+Math.random()*.4:0,
      drift:mode==='loading'?Math.random()*.3-.15:0,
      silver:mode==='loading'?false:Math.random()<.4,
      trail:Math.random()<trailRate,
      glow:Math.random()<profile.glowRate,
    }));
    shoots = [];
    const now = performance.now();
    initialShoots = mode === 'landing'
      ? profile.name === 'constrained' ? [now+1100] : [now+300,now+1400]
      : mode === 'funnel' ? [now+(profile.name === 'constrained' ? 1400 : 800)] : [];
    nextShoot = mode === 'landing' || mode === 'funnel' ? now+shootInterval() : Infinity;
  };

  const buildLandingBackground = (): void => {
    cachedLandingBackground = undefined;
    if (mode !== 'landing' || width <= 0 || height <= 0) return;
    const background = document.createElement('canvas');
    background.width = Math.round(width*ratio);
    background.height = Math.round(height*ratio);
    const buffer = background.getContext('2d');
    if (!buffer) return;
    buffer.setTransform(ratio,0,0,ratio,0,0);
    const base = buffer.createRadialGradient(width*.5,height*.38,0,width*.5,height*.5,width*.9);
    base.addColorStop(0,'#110c28');base.addColorStop(.35,'#0a0720');base.addColorStop(.7,'#060514');base.addColorStop(1,'#03030c');
    buffer.fillStyle=base;buffer.fillRect(0,0,width,height);
    const galaxy=buffer.createRadialGradient(width*.5,height*.42,0,width*.5,height*.42,width*.35);
    galaxy.addColorStop(0,'rgba(120,90,200,.08)');galaxy.addColorStop(.4,'rgba(80,60,160,.04)');galaxy.addColorStop(1,'rgba(0,0,0,0)');
    buffer.fillStyle=galaxy;buffer.fillRect(0,0,width,height);
    for(const nebula of landingNebulas){
      const x=nebula.x*width,y=nebula.y*height,r=nebula.radius*width;
      const glow=buffer.createRadialGradient(x,y,0,x,y,r);
      glow.addColorStop(0,nebula.color);glow.addColorStop(1,'rgba(0,0,0,0)');
      buffer.fillStyle=glow;buffer.beginPath();buffer.arc(x,y,r,0,Math.PI*2);buffer.fill();
    }
    cachedLandingBackground = background;
  };

  const background = (): void => {
    context.clearRect(0,0,width,height);
    if (mode === 'landing' && cachedLandingBackground) context.drawImage(cachedLandingBackground,0,0,width,height);
  };

  const resize = (): void => {
    const nextProfile = selectCelestialRenderingProfile({ width:innerWidth, height:innerHeight, devicePixelRatio });
    const profileChanged = nextProfile.name !== profile.name;
    profile = nextProfile;
    ratio = Math.min(devicePixelRatio||1,profile.dprCap);
    width=innerWidth;height=innerHeight;
    canvas.width=Math.round(width*ratio);canvas.height=Math.round(height*ratio);
    context.setTransform(ratio,0,0,ratio,0,0);
    if (profileChanged) build();
    buildLandingBackground();
    draw(performance.now(),true);
  };

  const spawnShoot = (): void => {
    const constrained = profile.name === 'constrained';
    const baseLength = mode === 'landing' ? (constrained ? 52 : 70) : (constrained ? 48 : 60);
    const spread = mode === 'landing' ? (constrained ? 72 : 110) : (constrained ? 65 : 100);
    const length=baseLength+Math.random()*spread;
    const speed=4+Math.random()*5;
    shoots.push({x:Math.random()*width*.7,y:Math.random()*height*.35,length,angle:(Math.PI/4)+Math.random()*.35,age:0,duration:(length/speed)*(1000/60),silver:Math.random()<.4});
  };

  const draw = (time:number, still=false): void => {
    const dt=Math.min(50,time-previous);previous=time;background();
    for(const star of stars){
      if(!still&&shouldAnimateCelestial(reduced.matches,document.hidden)){
        star.phase+=dt*.0018*star.twinkle;
        if(mode==='loading'){
          star.x+=star.drift*dt/(1000/60)/Math.max(width,1);star.y+=star.fall*dt/(1000/60)/Math.max(height,1);
          if(star.y>1.05){star.y=-.05;star.x=Math.random()*1.2-.1;}
        }
      }
      const alpha=.2+.72*(.5+.5*Math.sin(star.phase));
      const x=star.x*width,y=star.y*height,color=star.silver?'200,232,255':'245,202,98';
      if(star.trail){
        const trail=mode==='loading'?8:7;
        const gradient=context.createLinearGradient(x,y-trail,x,y);
        gradient.addColorStop(0,`rgba(${color},0)`);gradient.addColorStop(1,`rgba(${color},${alpha*.55})`);
        context.strokeStyle=gradient;context.lineWidth=.7;context.beginPath();context.moveTo(x,y-trail);context.lineTo(x,y);context.stroke();
      }
      if(star.glow){context.shadowColor=`rgba(${color},${alpha})`;context.shadowBlur=star.r*profile.shadowScale;}
      else context.shadowBlur=0;
      context.fillStyle=`rgba(${color},${alpha})`;context.beginPath();context.arc(x,y,star.r,0,Math.PI*2);context.fill();
    }
    context.shadowBlur=0;
    if(!still&&shouldAnimateCelestial(reduced.matches,document.hidden)){
      while(initialShoots.length&&time>=initialShoots[0]!){initialShoots.shift();spawnShoot();}
      if(time>=nextShoot){spawnShoot();nextShoot=time+shootInterval();}
    }
    for(const shoot of shoots){
      if(!still)shoot.age+=dt;
      const progress=Math.min(1,shoot.age/shoot.duration),distance=progress*shoot.length,x=shoot.x+distance*Math.cos(shoot.angle),y=shoot.y+distance*Math.sin(shoot.angle);
      const trail=Math.min(shoot.length*.65,distance),startX=x-trail*Math.cos(shoot.angle),startY=y-trail*Math.sin(shoot.angle),color=shoot.silver?'210,230,255':'255,245,200';
      const gradient=context.createLinearGradient(startX,startY,x,y);gradient.addColorStop(0,`rgba(${color},0)`);gradient.addColorStop(1,`rgba(${color},${(1-progress)*.95})`);
      context.strokeStyle=gradient;context.lineWidth=1.8;context.beginPath();context.moveTo(startX,startY);context.lineTo(x,y);context.stroke();
    }
    shoots=shoots.filter((shoot)=>shoot.age<shoot.duration);
  };

  const loop = (time:number): void => { draw(time);frame=requestAnimationFrame(loop); };
  const start = (): void => {
    cancelAnimationFrame(frame);
    if(mode==='none'){background();return;}
    if(!shouldAnimateCelestial(reduced.matches,document.hidden)){draw(performance.now(),true);return;}
    previous=performance.now();frame=requestAnimationFrame(loop);
  };
  const setMode = (next:CelestialMode): void => {
    if(mode===next)return;
    mode=next;build();buildLandingBackground();start();
  };
  const visibility = (): void => { if(document.hidden)cancelAnimationFrame(frame);else start(); };
  const motion = (): void => start();
  const stop = (): void => {
    cancelAnimationFrame(frame);removeEventListener('resize',resize);document.removeEventListener('visibilitychange',visibility);reduced.removeEventListener('change',motion);controller=undefined;cachedLandingBackground=undefined;
  };

  controller={setMode,stop};
  build();
  addEventListener('resize',resize,{passive:true});document.addEventListener('visibilitychange',visibility);reduced.addEventListener('change',motion);
  resize();start();return stop;
}
